import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  FormHelperText,
} from "@mui/material";
import landingApi from "../../contexts/landingApiClient";
import moment from "moment";
import { Formik } from "formik";
import * as Yup from "yup";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const ManagePayoutModal = ({
  open,
  handleClose,
  onPayoutCreated,
  payout,
  availableBalance,
  affiliate,
}) => {
  const { t } = useTranslation();
  const isEditing = Boolean(payout);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="manage-payout-modal-title"
      aria-describedby="manage-payout-modal-description"
    >
      <Box sx={style}>
        <Typography variant="h6" component="h2">
          {isEditing ? t("edit_payout") : t("create_payout")}
        </Typography>
        <Formik
          initialValues={{
            affiliate:
              payout?.attributes?.affiliate?.data?.id || affiliate?.id || "",
            amount: payout?.attributes.amount || "",
            status: payout?.attributes.status || "draft",
            appliedAt:
              moment(payout?.attributes.appliedAt).format("YYYY-MM-DD") ||
              moment().format("YYYY-MM-DD"),
            notes: payout?.attributes.notes || "",
            submit: null,
          }}
          enableReinitialize
          validateOnChange={true}
          validateOnBlur={true}
          validationSchema={Yup.object().shape({
            affiliate: Yup.string().required(
              t("validation_affiliate_required")
            ),
            amount: Yup.number()
              .positive(t("validation_amount_positive"))
              .required(t("validation_amount_required"))
              .max(
                availableBalance,
                t("validation_amount_exceeds_balance", {
                  balance: availableBalance.toFixed(2),
                })
              ),
            status: Yup.string().required(t("validation_status_required")),
            appliedAt: Yup.date().required(t("validation_date_required")),
            notes: Yup.string()
              .max(200, t("validation_notes_max_length", "Notes must be 200 characters or less"))
              .optional(),
          })}
          onSubmit={async (
            values,
            { setErrors, setStatus, setSubmitting, resetForm }
          ) => {
            try {
              const payload = {
                affiliate: values.affiliate,
                amount: parseFloat(values.amount),
                status: values.status,
                appliedAt: values.appliedAt,
                notes: values.notes,
                publishedAt: new Date(),
              };
              const requestData = {
                data: {
                  ...payload,
                  affiliate: affiliate.id,
                },
              };

              if (isEditing) {
                await landingApi.put(`/payouts/${payout.id}`, requestData);
              } else {
                await landingApi.post("/payouts", requestData);
              }
              if (onPayoutCreated) {
                onPayoutCreated();
              }
              resetForm();
              handleClose();
            } catch (error) {
              console.error(t("error_create_payout_failed"), error);
              setStatus({ success: false });
              setErrors({
                submit: error.message || t("error_create_payout_failed"),
              });
              setSubmitting(false);
            }
          }}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting,
            touched,
            values,
            isValid,
          }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Box mt={2}>
                <TextField
                  label={t("affiliate")}
                  fullWidth
                  margin="normal"
                  value={affiliate?.attributes?.email || ""}
                  disabled
                />
                <TextField
                  name="amount"
                  label={t("amount_with_currency")}
                  type="number"
                  fullWidth
                  margin="normal"
                  value={values.amount}
                  error={Boolean(errors.amount)}
                  helperText={errors.amount}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  disabled={isEditing && values.status === "paid"}
                />
                <FormControl fullWidth margin="normal">
                  <InputLabel>{t("status")}</InputLabel>
                  <Select
                    name="status"
                    value={values.status}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <MenuItem value="draft">{t("status_draft")}</MenuItem>
                    <MenuItem value="processing">
                      {t("status_processing")}
                    </MenuItem>
                    <MenuItem value="paid" disabled={!isEditing}>
                      {t("status_paid_capital")}
                    </MenuItem>
                    <MenuItem value="failed">{t("status_failed")}</MenuItem>
                    <MenuItem value="rejected">{t("status_rejected")}</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  name="appliedAt"
                  label={t("date")}
                  type="date"
                  fullWidth
                  margin="normal"
                  value={values.appliedAt}
                  error={Boolean(touched.appliedAt && errors.appliedAt)}
                  helperText={touched.appliedAt && errors.appliedAt}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <TextField
                  name="notes"
                  label={t("notes")}
                  multiline
                  rows={4}
                  fullWidth
                  margin="normal"
                  value={values.notes}
                  error={Boolean(errors.notes)}
                  helperText={errors.notes || `${values.notes.length}/200`}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  inputProps={{ maxLength: 200 }}
                />
                {errors.submit && (
                  <Box mt={2}>
                    <Typography color="error">{errors.submit}</Typography>
                  </Box>
                )}
                <Box mt={2} display="flex" justifyContent="flex-end">
                  <Button onClick={handleClose} sx={{ mr: 1 }}>
                    {t("cancel")}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting || !isValid}
                  >
                    {isEditing ? t("update") : t("create")}
                  </Button>
                </Box>
              </Box>
            </form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default ManagePayoutModal;
